/**
 * Simple History-based SPA router
 */

export interface Route {
  path: string;
  title: string;
  render: (container: HTMLElement) => void | Promise<void>;
}

let routes: Route[] = [];
let notFoundHandler: ((container: HTMLElement) => void) | null = null;

/** Register all routes */
export function registerRoutes(r: Route[]): void {
  routes = r;
}

/** Register a 404 handler */
export function registerNotFound(handler: (container: HTMLElement) => void): void {
  notFoundHandler = handler;
}

/** Get current route path */
export function getCurrentPath(): string {
  return window.location.pathname || '/';
}

/** Navigate to a path without full reload */
export function navigateTo(path: string): void {
  window.history.pushState({}, '', path);
  handleRoute();
}

/** Start listening for popstate and render the initial route */
export function startRouter(): void {
  window.addEventListener('popstate', () => handleRoute());
  
  // Intercept all clicks on <a> tags to use navigateTo if they are internal
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor && anchor.href && anchor.href.startsWith(window.location.origin)) {
      const url = new URL(anchor.href);
      const path = url.pathname;
      
      // If it's an internal path and doesn't explicitly want a new tab
      const targetAttr = anchor.getAttribute('target');
      if (!path.includes('.') && path !== '/' && targetAttr !== '_blank') {
        e.preventDefault();
        // Use the full relative path including query params
        const fullPath = url.pathname + url.search + url.hash;
        navigateTo(fullPath);
      }
    }
  });

  handleRoute();
}

/** Handle the current route */
export async function handleRoute(): Promise<void> {
  const path = getCurrentPath();
  const container = document.getElementById('app');
  if (!container) return;

  const route = routes.find((r) => r.path === path);
  if (route) {
    document.title = route.title;
    await route.render(container);
  } else if (notFoundHandler) {
    document.title = 'Page Not Found';
    notFoundHandler(container);
  }
}
